import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateUTC } from 'helpers/dateParser';
import { useBranding } from 'modules/Common/hooks/useBranding';
import { cn } from 'modules/Common/utils/cn';
import { applyBrand } from '../utils/releaseNotes';

// The one rendering of release notes (D10). Both surfaces — the Clerk
// org-profile tab and the post-login drawer — draw this; only the frame
// differs. Same shape as GuidingChecklistFloatingWidget reusing
// GuidingChecklistPanel rather than growing a second layout.
//
// Purely presentational: no query, no read state, no open/close. That is what
// lets phase 2 and phase 3 wrap it without touching it.
//
// Laid out as a vertical timeline: a rail down the left, one node per release,
// the version as a brand-tinted pill beside the date. The first entry is the
// newest (the endpoint serves newest first, D2) and wears a "Latest" chip.
export default function ReleaseNoteList({ releases = [] }) {
  const { t } = useTranslation('app');
  const brandName = useBranding((s) => s.name);

  // D3: an empty list is the ONLY empty state. There is deliberately no
  // "couldn't load release notes" message — a user who did not ask for release
  // notes should never be told they failed.
  if (!releases.length) return null;

  return (
    <ol className="relative m-0 flex list-none flex-col p-0">
      {releases.map((release, index) => {
        const isLatest = index === 0;
        const isLast = index === releases.length - 1;
        return (
          <li
            key={release.version}
            className={cn('relative ps-7', isLast ? 'pb-1' : 'pb-9')}
          >
            {/* The rail: a hairline from this node down to the next one. The
                last node has nothing below it, so no line. */}
            {!isLast ? (
              <span
                aria-hidden="true"
                className="absolute start-[7px] top-4 bottom-0 w-px bg-slate-200"
              />
            ) : null}
            {/* The node. The latest release gets the brand colour with a soft
                halo; older ones sit quietly in slate. */}
            <span
              aria-hidden="true"
              className={cn(
                'absolute start-0 top-[3px] flex h-[15px] w-[15px] items-center justify-center rounded-full',
                isLatest ? 'bg-primaryColor/15' : 'bg-transparent',
              )}
            >
              <span
                className={cn(
                  'block h-[7px] w-[7px] rounded-full',
                  isLatest ? 'bg-primaryColor' : 'bg-slate-300 ring-2 ring-white',
                )}
              />
            </span>

            <article>
              <header className="mb-3">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
                  <h3 className="m-0 inline-flex items-center rounded-full bg-primaryColor/10 px-2.5 py-0.5 font-mono text-[12px] font-semibold leading-5 tracking-tight text-primaryColor-dark">
                    {t('whatsNew.versionPill', 'v{{version}}', { version: release.version })}
                  </h3>
                  {isLatest ? (
                    <span className="inline-flex items-center rounded-full border border-primaryColor/30 px-2 py-0.5 text-[11px] font-medium uppercase leading-4 tracking-wide text-primaryColor-dark">
                      {t('whatsNew.latest', 'Latest')}
                    </span>
                  ) : null}
                  {/* A release date is a calendar day, not a moment: formatDateUTC
                      pins it to UTC so it cannot shift a day for a user east or
                      west of the release. */}
                  {release.releasedAt ? (
                    <time
                      dateTime={release.releasedAt}
                      className="ms-auto text-xs tabular-nums text-slate-400"
                    >
                      {formatDateUTC(release.releasedAt)}
                    </time>
                  ) : null}
                </div>
                {release.title ? (
                  <p className="mb-0 mt-2 text-[15px] font-semibold leading-snug tracking-tight text-slate-900">
                    {applyBrand(release.title, brandName)}
                  </p>
                ) : null}
              </header>

              {release.highlights.length ? (
                <ul className="m-0 flex list-none flex-col gap-3 p-0">
                  {/* Keyed by position, not by `highlight.id`. The id comes from the
                      server and is not guaranteed unique — two highlights sharing
                      one would collide, and the synthesised `${version}-${i}`
                      fallback can itself collide with a real server id of that
                      shape. The list is static and never reordered, so the index is
                      both unique and stable. */}
                  {release.highlights.map((highlight, i) => (
                    <li
                      key={i}
                      className="relative ps-4"
                    >
                      <span
                        aria-hidden="true"
                        className="absolute start-0 top-[9px] h-1.5 w-1.5 rounded-full bg-primaryColor/60"
                      />
                      <span className="block text-sm font-medium leading-6 text-slate-800">
                        {applyBrand(highlight.title, brandName)}
                      </span>
                      {highlight.body ? (
                        <span className="block text-[13px] leading-5 text-slate-500">
                          {applyBrand(highlight.body, brandName)}
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : null}
            </article>
          </li>
        );
      })}
    </ol>
  );
}
